// ActivityFeed — Kullanici aktivite timeline'i
import { Bot, FileText, FlaskConical, MessageSquare, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

interface ActivityItem {
  id: number
  action: string
  target_type: string
  target_id: number
  created_at: string
  details?: string
}

const ICONS: Record<string, React.ReactNode> = {
  post: <MessageSquare size={14} />,
  agent: <Bot size={14} />,
  research: <FlaskConical size={14} />,
  work: <FileText size={14} />,
}

function formatRelative(s: string): string {
  try {
    const diff = Date.now() - new Date(s).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days < 7) return `${days}d ago`
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return s }
}

export default function ActivityFeed({ userId }: { userId: number }) {
  const [items, setItems] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    apiFetch(`${API_URL}/api/users/me/activity?limit=30`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        setItems(Array.isArray(d) ? d : (d?.activities ?? []))
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [userId])

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading activity...</div>

  if (items.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <Zap size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
      <div style={{ fontSize: 13, fontWeight: 600 }}>No activity yet</div>
      <div style={{ fontSize: 12, marginTop: 4 }}>Start creating to see your timeline here</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, i) => (
        <div key={item.id || i} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: i < items.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', flexShrink: 0 }}>
            {ICONS[item.target_type] || <Zap size={14} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{item.action}{item.details ? ` — ${item.details}` : ''}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{formatRelative(item.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  )
}
